import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Route, MapPin, Trash2 } from "lucide-react";

const STATUS_PILL = { scheduled: "forest", in_progress: "warn", completed: "success", cancelled: "error" };
const WASTE_TYPES = ["general", "recyclable", "organic", "hazardous"];

const emptyStop = { client_id: "", site_id: "", waste_type: "general", notes: "" };
const emptyTrip = {
  driver: "",
  driver_user_id: "",
  vehicle: "",
  vehicle_id: "",
  trip_date: new Date().toISOString().slice(0, 10),
  start_time: "08:00",
  end_time: "16:00",
  stops: [],
  notes: "",
};

export default function Trips() {
  const [trips, setTrips] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientMap, setClientMap] = useState({});
  const [sites, setSites] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyTrip);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/trips").then((r) => setTrips(r.data));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => {
      setClients(r.data);
      const m = {};
      r.data.forEach((c) => (m[c.id] = c.name));
      setClientMap(m);
    });
    api.get("/sites").then((r) => setSites(r.data));
    api.get("/drivers").then((r) => setDrivers(r.data.filter((d) => d.active && d.has_password))).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data.filter((v) => v.active))).catch(() => {});
  }, []);

  const sitesForClient = (cid) => sites.filter((s) => s.client_id === cid);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        stops: form.stops.map((s) => ({ ...s, site_id: s.site_id || null })),
      };
      if (editingId) {
        await api.put(`/trips/${editingId}`, payload);
        toast.success("Trip updated");
      } else {
        await api.post("/trips", payload);
        toast.success("Trip scheduled");
      }
      setShowForm(false); setEditingId(null); setForm(emptyTrip); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const edit = (t) => {
    setForm({ ...t, stops: t.stops || [], start_time: t.start_time || "", end_time: t.end_time || "" });
    setEditingId(t.id); setShowForm(true);
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete trip ${t.trip_number}?`)) return;
    await api.delete(`/trips/${t.id}`); toast.success("Deleted"); load();
  };
  const setStatus = async (t, status) => { await api.patch(`/trips/${t.id}`, { status }); load(); };

  return (
    <div>
      <PageHeader
        eyebrow="Operations"
        title="Trips & Routes"
        subtitle="Schedule driver routes, assign stops and track completion in real time."
        actions={
          <button
            onClick={() => { setForm({ ...emptyTrip, stops: [] }); setEditingId(null); setShowForm(true); }}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="add-trip-btn"
          >
            <Plus className="w-4 h-4" /> Schedule trip
          </button>
        }
      />
      <div className="px-8 lg:px-12 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
          {trips.map((t) => (
            <div key={t.id} className="hbt-card p-6" data-testid={`trip-card-${t.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-md grid place-items-center" style={{ background: "rgba(26,54,45,0.08)", color: "var(--forest)" }}>
                  <Route className="w-5 h-5" strokeWidth={1.6} />
                </div>
                <select value={t.status} onChange={(e) => setStatus(t, e.target.value)} className={`pill ${STATUS_PILL[t.status] || ""} text-[11px] py-1 pr-6 pl-3 border-0 cursor-pointer`} data-testid={`trip-status-${t.id}`}>
                  {["scheduled","in_progress","completed","cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="mt-5">
                <div className="font-mono text-[11px] text-neutral-500">{t.trip_number}</div>
                <div className="font-display text-lg font-semibold tracking-tight mt-1">{t.driver} · {t.vehicle}</div>
                <div className="text-xs text-neutral-500 mt-1 font-mono-num">
                  {t.trip_date}{t.start_time && ` · ${t.start_time}`}{t.end_time && `–${t.end_time}`}
                </div>
              </div>
              {t.stops?.length > 0 && (
                <div className="mt-5 space-y-1">
                  <div className="hbt-label">Stops ({t.stops.length})</div>
                  {t.stops.slice(0, 3).map((s, i) => (
                    <div key={`${t.id}-stop-${s.client_id || i}-${i}`} className="text-xs flex items-center gap-2 py-1">
                      <MapPin className="w-3 h-3 text-neutral-400 shrink-0" />
                      <span className="font-medium">{clientMap[s.client_id] || "—"}</span>
                      {s.waste_type && <span className="pill forest ml-auto shrink-0">{s.waste_type}</span>}
                    </div>
                  ))}
                  {t.stops.length > 3 && <div className="text-[11px] text-neutral-400">+{t.stops.length - 3} more…</div>}
                </div>
              )}
              <div className="mt-5 flex items-center gap-2">
                <button onClick={() => edit(t)} className="btn-outline text-xs" data-testid={`edit-trip-${t.id}`}>Edit</button>
                <button onClick={() => remove(t)} className="btn-outline text-xs" data-testid={`delete-trip-${t.id}`}>Delete</button>
              </div>
            </div>
          ))}
        </div>
        {trips.length === 0 && <div className="hbt-card p-16 text-center text-neutral-500">No trips scheduled yet.</div>}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="hbt-card p-8 w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="trip-form-modal">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="hbt-label">{editingId ? "Edit" : "New"} trip</div>
                <div className="font-display text-2xl font-semibold mt-1">{editingId ? "Update trip" : "Schedule a trip"}</div>
              </div>
              <button onClick={() => setShowForm(false)} className="text-neutral-400 hover:text-black"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="hbt-label block mb-2">Driver</label>
                  <select required className="hbt-input" value={form.driver_user_id || ""} onChange={(e) => {
                    const d = drivers.find((x) => x.user_id === e.target.value);
                    setForm({ ...form, driver_user_id: e.target.value, driver: d?.name || "", vehicle_id: d?.assigned_vehicle_id || form.vehicle_id, vehicle: vehicles.find((v) => v.id === d?.assigned_vehicle_id)?.registration || form.vehicle });
                  }} data-testid="trip-driver-select">
                    <option value="">Select a driver…</option>
                    {drivers.map((d) => <option key={d.user_id} value={d.user_id}>{d.name}{d.phone ? ` · ${d.phone}` : ""}</option>)}
                  </select>
                  {drivers.length === 0 && <div className="text-[11px] text-neutral-500 mt-1">No active drivers. <a href="/app/drivers" className="underline" style={{ color: "var(--forest)" }}>Add one →</a></div>}
                </div>
                <div><label className="hbt-label block mb-2">Vehicle</label>
                  <select required className="hbt-input" value={form.vehicle_id || ""} onChange={(e) => {
                    const v = vehicles.find((x) => x.id === e.target.value);
                    setForm({ ...form, vehicle_id: e.target.value, vehicle: v?.registration || "" });
                  }} data-testid="trip-vehicle-select">
                    <option value="">Select a truck…</option>
                    {vehicles.map((v) => <option key={v.id} value={v.id}>{v.registration}{v.make_model ? ` · ${v.make_model}` : ""}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="hbt-label block mb-2">Date</label>
                  <input type="date" required className="hbt-input" value={form.trip_date} onChange={(e) => setForm({ ...form, trip_date: e.target.value })} data-testid="trip-date-input" /></div>
                <div><label className="hbt-label block mb-2">Start</label>
                  <input type="time" className="hbt-input" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="trip-start-input" /></div>
                <div><label className="hbt-label block mb-2">End</label>
                  <input type="time" className="hbt-input" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="trip-end-input" /></div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="hbt-label">Stops</label>
                  <button type="button" onClick={() => setForm({ ...form, stops: [...form.stops, { ...emptyStop }] })} className="text-xs text-[var(--forest)] font-semibold hover:underline inline-flex items-center gap-1" data-testid="add-stop-btn">
                    <Plus className="w-3 h-3" /> Add stop
                  </button>
                </div>
                <div className="space-y-2">
                  {form.stops.map((s, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end p-3 border rounded" style={{ borderColor: "var(--border)" }} data-testid={`stop-row-${i}`}>
                      <div className="col-span-4">
                        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Client</label>
                        <select required className="hbt-input mt-1" value={s.client_id} onChange={(e) => {
                          const stops = [...form.stops]; stops[i] = { ...stops[i], client_id: e.target.value, site_id: "" }; setForm({ ...form, stops });
                        }} data-testid={`stop-client-${i}`}>
                          <option value="">—</option>
                          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>
                      <div className="col-span-4">
                        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Site</label>
                        <select className="hbt-input mt-1" value={s.site_id || ""} onChange={(e) => {
                          const stops = [...form.stops]; stops[i] = { ...stops[i], site_id: e.target.value }; setForm({ ...form, stops });
                        }} data-testid={`stop-site-${i}`}>
                          <option value="">Any</option>
                          {sitesForClient(s.client_id).map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
                        </select>
                      </div>
                      <div className="col-span-3">
                        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Type</label>
                        <select className="hbt-input mt-1" value={s.waste_type} onChange={(e) => {
                          const stops = [...form.stops]; stops[i] = { ...stops[i], waste_type: e.target.value }; setForm({ ...form, stops });
                        }} data-testid={`stop-type-${i}`}>
                          {WASTE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <button type="button" onClick={() => setForm({ ...form, stops: form.stops.filter((_, idx) => idx !== i) })} className="col-span-1 p-2 text-neutral-400 hover:text-red-700" data-testid={`stop-remove-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {form.stops.length === 0 && <div className="text-xs text-neutral-400 py-2">No stops added yet.</div>}
                </div>
              </div>

              <div><label className="hbt-label block mb-2">Notes</label>
                <textarea rows={2} className="hbt-input" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="trip-notes-input" /></div>

              <div className="flex justify-end gap-2 pt-3">
                <button type="button" onClick={() => setShowForm(false)} className="btn-outline">Cancel</button>
                <button type="submit" className="btn-primary" data-testid="save-trip">{editingId ? "Update" : "Schedule"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
